const parseYouTubeStartSeconds = (rawValue) => {
  if (!rawValue) {
    return null;
  }

  const value = decodeURIComponent(String(rawValue).trim());

  if (/^\d+$/.test(value)) {
    return parseInt(value, 10);
  }

  const bareSeconds = value.match(/^(\d+)s?$/i);

  if (bareSeconds) {
    return parseInt(bareSeconds[1], 10);
  }

  let seconds = 0;
  const hours = value.match(/(\d+)h/i);
  const minutes = value.match(/(\d+)m/i);
  const secondsPart = value.match(/(\d+)s/i);

  if (hours) {
    seconds += parseInt(hours[1], 10) * 3600;
  }

  if (minutes) {
    seconds += parseInt(minutes[1], 10) * 60;
  }

  if (secondsPart) {
    seconds += parseInt(secondsPart[1], 10);
  }

  return seconds > 0 ? seconds : null;
};

const parseYouTubeUrlParams = (originalUrl) => {
  const params = {
    start: null,
    cc_load_policy: null,
    cc_lang_pref: null,
    hl: null
  };

  try {
    const normalizedUrl = originalUrl.startsWith('http') ? originalUrl : `https://${originalUrl}`;
    const parsedUrl = new URL(normalizedUrl);

    parsedUrl.searchParams.forEach((val, key) => {
      if (key === 't' || key === 'start') {
        params.start = parseYouTubeStartSeconds(val);
      } else if (key === 'cc_load_policy') {
        params.cc_load_policy = val;
      } else if (key === 'cc_lang_pref') {
        params.cc_lang_pref = val;
      } else if (key === 'hl') {
        params.hl = val;
      }
    });
  } catch (error) {
    // Keep defaults when the source URL cannot be parsed.
  }

  if (params.cc_lang_pref && !params.hl) {
    params.hl = params.cc_lang_pref;
  }

  return params;
};

const buildYouTubePlayerVars = (originalUrl, origin) => {
  const youtubeParams = parseYouTubeUrlParams(originalUrl);
  const playerVars = {
    playsinline: 1,
    controls: 1,
    enablejsapi: 1,
    rel: 0,
    modestbranding: 1,
    origin: origin || window.location.origin
  };

  if (youtubeParams.start != null) {
    playerVars.start = youtubeParams.start;
  }

  if (youtubeParams.cc_load_policy != null) {
    playerVars.cc_load_policy = Number(youtubeParams.cc_load_policy) || 1;
  }

  if (youtubeParams.cc_lang_pref != null) {
    playerVars.cc_lang_pref = youtubeParams.cc_lang_pref;
  }

  if (youtubeParams.hl != null) {
    playerVars.hl = youtubeParams.hl;
  }

  return playerVars;
};

const onYouTubePromise = new Promise((resolve) => {
  if (window.YT?.Player) {
    resolve();
    return;
  }

  const previousReadyHandler = window.onYouTubeIframeAPIReady;

  window.onYouTubeIframeAPIReady = () => {
    previousReadyHandler?.();
    resolve();
  };

  if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  }
});

const applyYouTubeCaptions = (player, languageCode) => {
  if (!languageCode || typeof player.setOption !== 'function') {
    return false;
  }

  const normalizedLanguage = languageCode.toLowerCase().split('-')[0];

  try {
    const tracklist = player.getOption?.('captions', 'tracklist') || [];
    const matchingTrack = tracklist.find((track) => {
      const code = (track.languageCode || '').toLowerCase();
      return code === languageCode.toLowerCase() || code === normalizedLanguage || code.startsWith(`${normalizedLanguage}-`);
    });

    if (matchingTrack) {
      player.setOption('captions', 'track', matchingTrack);
    } else {
      player.setOption('captions', 'track', { languageCode: normalizedLanguage });
    }

    player.setOption('captions', 'reload', true);
    return true;
  } catch (error) {
    return false;
  }
};

const scheduleYouTubeCaptions = (player, languageCode) => {
  if (!languageCode) {
    return;
  }

  const apply = () => {
    applyYouTubeCaptions(player, languageCode);
  };

  apply();
  window.requestAnimationFrame(apply);
  window.setTimeout(apply, 250);
  window.setTimeout(apply, 1000);
};

const playYouTubeModalVideo = async (videoMedia) => {
  const playerTarget = videoMedia.querySelector('.youtube-video-modal__player');

  if (!playerTarget) {
    return null;
  }

  await onYouTubePromise;

  const youtubeId = videoMedia.dataset.youtubeId;
  const youtubeUrl = videoMedia.dataset.youtubeUrl;
  const captionLanguage = parseYouTubeUrlParams(youtubeUrl).cc_lang_pref;

  return new Promise((resolve) => {
    if (videoMedia._youtubeModalPlayer?.destroy) {
      try {
        videoMedia._youtubeModalPlayer.destroy();
      } catch (error) {
        // Player may already be destroyed between modal sessions.
      }

      videoMedia._youtubeModalPlayer = null;
      playerTarget.innerHTML = '';
    }

    videoMedia._youtubeModalPlayer = new YT.Player(playerTarget, {
      videoId: youtubeId,
      playerVars: buildYouTubePlayerVars(youtubeUrl, window.location.origin),
      events: {
        onReady: (event) => {
          videoMedia.setAttribute('loaded', '');
          videoMedia.setAttribute('can-play', '');
          scheduleYouTubeCaptions(event.target, captionLanguage);
          event.target.playVideo();
          videoMedia.setAttribute('playing', '');
          resolve(event.target);
        },
        onApiChange: (event) => {
          scheduleYouTubeCaptions(event.target, captionLanguage);
        },
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.PLAYING) {
            videoMedia.setAttribute('playing', '');
            scheduleYouTubeCaptions(event.target, captionLanguage);
          } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
            videoMedia.removeAttribute('playing');
          }
        }
      }
    });
  });
};

const stopYouTubeModalPlayer = (videoMedia) => {
  const player = videoMedia?._youtubeModalPlayer;
  const playerTarget = videoMedia?.querySelector('.youtube-video-modal__player');

  if (player) {
    try {
      if (typeof player.stopVideo === 'function') {
        player.stopVideo();
      } else if (typeof player.pauseVideo === 'function') {
        player.pauseVideo();
      }
    } catch (error) {
      // Player may already be destroyed.
    }

    try {
      player.destroy?.();
    } catch (error) {
      // Player may already be destroyed.
    }

    videoMedia._youtubeModalPlayer = null;
  }

  if (playerTarget) {
    playerTarget.innerHTML = '';
  }

  videoMedia?.removeAttribute('playing');
  videoMedia?.removeAttribute('loaded');
  videoMedia?.removeAttribute('can-play');
};

const pauseYouTubeVideoModal = (drawer) => {
  const videoMedia = drawer.querySelector('video-media');

  if (!videoMedia) {
    return;
  }

  if (videoMedia.dataset.youtubeUrl) {
    stopYouTubeModalPlayer(videoMedia);
    return;
  }

  videoMedia.pause();

  const video = videoMedia.querySelector('video');

  if (video) {
    video.currentTime = 0;
  }
};

const loadYouTubeVideoModal = (drawer) => {
  const videoMedia = drawer.querySelector('video-media');

  if (!videoMedia) {
    return null;
  }

  const template = videoMedia.querySelector('template');

  if (template && !videoMedia.dataset.youtubeModalLoaded) {
    template.replaceWith(template.content.firstElementChild.cloneNode(true));
    videoMedia.dataset.youtubeModalLoaded = 'true';
  }

  return videoMedia;
};

const findYouTubeModalTrigger = (drawer) => {
  if (!drawer?.id) {
    return null;
  }

  return document.querySelector(`[aria-controls="${drawer.id}"]`);
};

const pauseTriggerPreviewVideo = (trigger) => {
  if (!trigger) {
    return;
  }

  trigger.querySelectorAll('video-media[preview-only]').forEach((videoMedia) => {
    videoMedia.pause();

    const video = videoMedia.querySelector('video');

    if (video) {
      video.pause();
    }
  });
};

const resumeTriggerPreviewVideo = (trigger) => {
  if (!trigger) {
    return;
  }

  trigger.querySelectorAll('video-media[preview-only][autoplay]').forEach((videoMedia) => {
    const playPromise = videoMedia.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {});
    }
  });
};

const initYouTubeVideoModals = () => {
  document.querySelectorAll('x-drawer.youtube-video-modal').forEach((drawer) => {
    if (drawer.dataset.youtubeModalReady) {
      return;
    }

    drawer.dataset.youtubeModalReady = 'true';

    Object.defineProperty(drawer, 'openFrom', {
      get: () => 'center',
      configurable: true
    });

    drawer._setInitialPosition = function () {
      this.style.top = '0';
      this.style.left = '0';
      this.style.right = '0';
      this.style.bottom = '0';
      this.style.width = '100%';
      this.style.height = '100%';
      this.style.transform = '';
    };

    drawer._getClipPathProperties = function () {
      return ['inset(50% round var(--rounded-sm))', 'inset(0 0 0 0 round var(--rounded-sm))'];
    };

    drawer.addEventListener('dialog:after-show', async () => {
      pauseTriggerPreviewVideo(findYouTubeModalTrigger(drawer));

      const videoMedia = loadYouTubeVideoModal(drawer);

      if (!videoMedia) {
        return;
      }

      if (videoMedia.dataset.youtubeUrl) {
        await playYouTubeModalVideo(videoMedia);
        return;
      }

      videoMedia.play();
    });

    drawer.addEventListener('dialog:after-hide', () => {
      pauseYouTubeVideoModal(drawer);
      resumeTriggerPreviewVideo(findYouTubeModalTrigger(drawer));
    });

    drawer._youtubeModalCloseObserver?.disconnect();
    drawer._youtubeModalCloseObserver = new MutationObserver(() => {
      if (!drawer.hasAttribute('open')) {
        pauseYouTubeVideoModal(drawer);
        resumeTriggerPreviewVideo(findYouTubeModalTrigger(drawer));
      }
    });
    drawer._youtubeModalCloseObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  });

  document.querySelectorAll('[aria-controls]').forEach((trigger) => {
    const drawerId = trigger.getAttribute('aria-controls');
    const drawer = drawerId ? document.getElementById(drawerId) : null;

    if (!drawer?.classList.contains('youtube-video-modal')) {
      return;
    }

    if (trigger.dataset.youtubeModalTriggerReady) {
      return;
    }

    trigger.dataset.youtubeModalTriggerReady = 'true';

    trigger.addEventListener('click', (event) => {
      if (event.target.closest('a[href], button:not(.video-play-button)')) {
        event.stopPropagation();
        return;
      }

      if (!drawer.open) {
        event.preventDefault();
        event.stopPropagation();
        pauseTriggerPreviewVideo(trigger);
        drawer.show();
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        trigger.click();
      }
    });
  });
};

await customElements.whenDefined('x-drawer');
initYouTubeVideoModals();
document.addEventListener('shopify:section:load', initYouTubeVideoModals);
